import {useState, useEffect, useRef} from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import TourIcon from '@mui/icons-material/Tour';
import HotelIcon from '@mui/icons-material/Hotel';
import CategoryIcon from '@mui/icons-material/Category';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HikingIcon from '@mui/icons-material/Hiking';
import Grid from '@mui/material/Grid2';
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

// Display-level categories. Backend keeps fine-grained keys (entertainment_and_recreation,
// arts_and_entertainment, outdoor_and_nature) but the frontend collapses them into "Attraction".
const ATTRACTION = { label: 'Attraction', icon: <TourIcon sx={{ fontSize: 14 }} /> };

export const CATEGORIES = {
  food_and_drink: { label: 'Food & Drink', icon: <RestaurantIcon sx={{ fontSize: 14 }} /> },
  shopping: { label: 'Shopping', icon: <ShoppingBagIcon sx={{ fontSize: 14 }} /> },
  entertainment_and_recreation: ATTRACTION,
  arts_and_entertainment: ATTRACTION,
  outdoor_and_nature: ATTRACTION,
  lodging: { label: 'Lodging', icon: <HotelIcon sx={{ fontSize: 14 }} /> },
  other: { label: 'Other', icon: <CategoryIcon sx={{ fontSize: 14 }} /> },
};

export const UNKNOWN_CATEGORY = { label: 'Unknown', icon: <HelpOutlineIcon sx={{ fontSize: 14 }} /> };

export const getCategoryDisplay = (category) => {
  if (!category) return UNKNOWN_CATEGORY;
  return CATEGORIES[category] || UNKNOWN_CATEGORY;
};

// Sub-review relation labels + icons. Keep aligned with the backend enum in
// ent/schema/entspotreview.go.
export const RELATIONS = {
  parking:   { label: 'Parking',   icon: <LocalParkingIcon sx={{ fontSize: 14 }} /> },
  viewpoint: { label: 'Viewpoint', icon: <VisibilityIcon sx={{ fontSize: 14 }} /> },
  trailhead: { label: 'Trailhead', icon: <HikingIcon sx={{ fontSize: 14 }} /> },
};

export const getRelationDisplay = (relation) => {
  if (!relation) return null;
  return RELATIONS[relation] || null;
};

// Priority order: Food & Drink > Lodging > Nature & Outdoors > Entertainment > Shopping > Arts & Culture
const CATEGORY_PRIORITY = [
  {
    key: 'food_and_drink',
    direct: ['food_and_drink'],
    legacy: ['restaurant', 'cafe', 'bar', 'bakery', 'food', 'meal_takeaway', 'ice_cream_shop', 'coffee_shop'],
  },
  {
    key: 'lodging',
    direct: ['lodging'],
    legacy: ['lodging', 'hotel', 'resort_hotel'],
  },
  {
    key: 'outdoor_and_nature',
    direct: ['outdoor_and_nature'],
    legacy: ['park', 'campground', 'national_park', 'hiking_area'],
  },
  {
    key: 'entertainment_and_recreation',
    direct: ['entertainment_and_recreation'],
    legacy: ['movie_theater', 'gym', 'amusement_park', 'night_club', 'bowling_alley', 'spa'],
  },
  {
    key: 'shopping',
    direct: ['shopping'],
    legacy: ['store', 'shopping_mall', 'clothing_store', 'book_store', 'grocery_store'],
  },
  {
    key: 'arts_and_entertainment',
    direct: ['arts_and_entertainment'],
    legacy: ['museum', 'art_gallery', 'performing_arts_theater', 'tourist_attraction'],
  },
];

export const resolveCategory = (placeTypes) => {
  if (!placeTypes || placeTypes.length === 0) return 'other';
  const typeSet = new Set(placeTypes);
  for (const cat of CATEGORY_PRIORITY) {
    for (const d of cat.direct) {
      if (typeSet.has(d)) return cat.key;
    }
    for (const l of cat.legacy) {
      if (typeSet.has(l)) return cat.key;
    }
  }
  return 'other';
};


const PlaceAutoComplete = ({ dataModel, setDataModel, placeName, setPlaceName, setPlaceAddress, geo, setGeo, onTypesResolved }) => {
  const placesLib = useMapsLibrary("places");
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const geoRef = useRef(geo);
  geoRef.current = geo;

  useEffect(() => {
    if (!placesLib || inputValue === "") {
      setOptions(value ? [value] : []);
      return;
    }

    const fetchSuggestions = async () => {
      try {
        const request = { input: inputValue };
        const { suggestions } = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        let newOptions = value ? [value] : [];
        if (suggestions) {
          const predictions = suggestions
            .filter((s) => s.placePrediction)
            .map((s) => s.placePrediction);
          // Sort: known categories first, "other" last
          predictions.sort((a, b) => {
            const catA = resolveCategory(a.types || []);
            const catB = resolveCategory(b.types || []);
            if (catA !== 'other' && catB === 'other') return -1;
            if (catA === 'other' && catB !== 'other') return 1;
            return 0;
          });
          newOptions = [...newOptions, ...predictions];
        }
        setOptions(newOptions);
      } catch (err) {
        console.error("autocomplete error", err);
      }
    };

    const timer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, placesLib]);

  // When a place is selected, fetch its details
  useEffect(() => {
    if (!placesLib || !value || !value.placeId) return;

    const fetchDetails = async () => {
      try {
        const place = new placesLib.Place({ id: value.placeId });
        await place.fetchFields({ fields: ["location", "types", "displayName", "formattedAddress"] });

        const loc = place.location;
        if (loc) {
          setGeo({ lat: loc.lat(), lng: loc.lng() });
        }

        setDataModel({
          ...dataModel,
          placeId: value.placeId,
        });
        setPlaceName(value.text?.text || inputValue);
        // Stash the formatted address too so draft sub-reviews can display
        // it (and link to Google Maps) before they've been saved.
        if (setPlaceAddress) {
          setPlaceAddress(place.formattedAddress || "");
        }

        if (onTypesResolved && place.types) {
          onTypesResolved(place.types);
        }
      } catch (err) {
        console.error("place details error", err);
      }
    };

    fetchDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, placesLib]);

  return (
    <Autocomplete
      sx={{ width: '100%', maxWidth: 480 }}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        return option.text?.text || option.description || "";
      }}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={placeName ?? value}
      noOptionsText="No locations"
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
      }}
      onInputChange={(event, newInputValue, reason) => {
        setInputValue(newInputValue);
        if (reason === "clear") {
          setDataModel({
            ...dataModel,
            placeId: null
          });
          setPlaceName("");
          if (setPlaceAddress) setPlaceAddress("");
          if (onTypesResolved) onTypesResolved([]);
        }
      }}
      renderInput={(params) => (
        <TextField {...params} label="Add a location" fullWidth required />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const mainText = option.mainText?.text || option.structured_formatting?.main_text || "";
        const secondaryText = option.secondaryText?.text || option.structured_formatting?.secondary_text || "";
        const optionTypes = option.types || [];
        const category = resolveCategory(optionTypes);
        const catInfo = CATEGORIES[category];

        // New API uses startOffset/endOffset, old uses offset/length
        const rawMatches = option.mainText?.matches || option.structured_formatting?.main_text_matched_substrings || [];
        const highlights = rawMatches.map((match) => {
          if (match.startOffset !== undefined && match.endOffset !== undefined) {
            return [match.startOffset, match.endOffset];
          }
          return [match.offset, match.offset + match.length];
        });

        const parts = parse(mainText, highlights);
        return (
          <li key={key} {...optionProps}>
            <Grid container sx={{ alignItems: "center" }}>
              <Grid sx={{ display: "flex", width: 44 }}>
                <LocationOnIcon sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid
                item
                sx={{ width: "calc(100% - 44px)", display: 'flex', alignItems: 'center' }}
              >
                <Box sx={{ minWidth: 0, flex: 1, wordWrap: "break-word" }}>
                  <Box>
                    {parts.map((part, index) => (
                      <Box
                        key={index}
                        component="span"
                        sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                      >
                        {part.text}
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {secondaryText}
                  </Typography>
                </Box>
                {catInfo && (
                  <Chip
                    icon={catInfo.icon}
                    label={catInfo.label}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0, ml: 1 }}
                  />
                )}
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}

export default PlaceAutoComplete;
